export const TRANSPORT_MODES = ['car', 'train', 'plane', 'bus'];

export function emptyTravelLeg() {
  return {
    departureCountry: '',
    departureCity: '',
    date: '',
    arrivalTime: '',
    transportMode: '',
    licensePlate: '',
    trainNumber: '',
    trainStation: '',
    trainStationCity: '',
    flightNumber: '',
    airport: '',
    busDeparturePlace: '',
  };
}

function formatDateForInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function travelLegFromApi(leg) {
  if (!leg) return emptyTravelLeg();
  return {
    departureCountry: leg.departureCountry || '',
    departureCity: leg.departureCity || '',
    date: formatDateForInput(leg.date),
    arrivalTime: leg.arrivalTime || '',
    transportMode: leg.transportMode || '',
    licensePlate: leg.licensePlate || '',
    trainNumber: leg.trainNumber || '',
    trainStation: leg.trainStation || '',
    trainStationCity: leg.trainStationCity || '',
    flightNumber: leg.flightNumber || '',
    airport: leg.airport || '',
    busDeparturePlace: leg.busDeparturePlace || '',
  };
}

export function cloneTravelLeg(leg) {
  return { ...(leg || emptyTravelLeg()) };
}

export function buildTravelFormStateFromOrder(order) {
  const attendees = Array.isArray(order?.attendees) ? order.attendees : [];
  const saved = Array.isArray(order?.travelInformation?.perAttendee)
    ? order.travelInformation.perAttendee
    : [];
  const savedByIndex = new Map(saved.map((row) => [row.attendeeIndex, row]));
  const hasSavedTravel = Boolean(order?.travelInformation?.waiverAccepted);

  const rows = attendees.map((attendee, attendeeIndex) => {
    const existing = savedByIndex.get(attendeeIndex);
    return {
      attendeeIndex,
      firstName: attendee.firstName || '',
      lastName: attendee.lastName || '',
      outbound: travelLegFromApi(existing?.outbound),
      returnLeg: travelLegFromApi(existing?.returnLeg),
    };
  });

  if (rows.length > 1 && !hasSavedTravel) {
    const primary = rows[0];
    for (let i = 1; i < rows.length; i += 1) {
      rows[i] = {
        ...rows[i],
        outbound: cloneTravelLeg(primary.outbound),
        returnLeg: cloneTravelLeg(primary.returnLeg),
      };
    }
  }

  return rows;
}

/** Recopie le voyage de la première personne vers les autres (sauf lignes modifiées manuellement). */
export function syncRowsFromPrimary(rows, customizedIndices) {
  if (!rows?.length || rows.length <= 1) return rows;
  const primary = rows[0];
  const skip = customizedIndices instanceof Set ? customizedIndices : new Set();

  return rows.map((row, index) => {
    if (index === 0 || skip.has(index)) return row;
    return {
      ...row,
      outbound: cloneTravelLeg(primary.outbound),
      returnLeg: cloneTravelLeg(primary.returnLeg),
    };
  });
}

export function copyTravelBetweenAttendees(rows, sourceIndex, targetIndices) {
  const source = rows[sourceIndex];
  if (!source) return rows;

  return rows.map((row, index) => {
    if (!targetIndices.includes(index) || index === sourceIndex) return row;
    return {
      ...row,
      outbound: cloneTravelLeg(source.outbound),
      returnLeg: cloneTravelLeg(source.returnLeg),
    };
  });
}

function isAlphanumericTransportId(value) {
  const s = String(value || '').trim();
  return s.length > 0 && /^[A-Za-z0-9]+$/.test(s);
}

export function validateTravelLegClient(leg, legLabel, t) {
  if (!leg.departureCountry?.trim()) return t('ticketTravel.validation.departureCountry', { leg: legLabel });
  if (!leg.departureCity?.trim()) return t('ticketTravel.validation.departureCity', { leg: legLabel });
  if (!leg.date?.trim()) return t('ticketTravel.validation.date', { leg: legLabel });
  if (!leg.arrivalTime?.trim()) return t('ticketTravel.validation.arrivalTime', { leg: legLabel });
  if (!TRANSPORT_MODES.includes(leg.transportMode)) {
    return t('ticketTravel.validation.transportMode', { leg: legLabel });
  }

  if (leg.transportMode === 'car' && !leg.licensePlate?.trim()) {
    return t('ticketTravel.validation.licensePlate', { leg: legLabel });
  }
  if (leg.transportMode === 'train') {
    if (!isAlphanumericTransportId(leg.trainNumber)) {
      return t('ticketTravel.validation.trainNumber', { leg: legLabel });
    }
    if (!leg.trainStation?.trim()) return t('ticketTravel.validation.trainStation', { leg: legLabel });
    if (!leg.trainStationCity?.trim()) {
      return t('ticketTravel.validation.trainStationCity', { leg: legLabel });
    }
  }
  if (leg.transportMode === 'plane') {
    if (!isAlphanumericTransportId(leg.flightNumber)) {
      return t('ticketTravel.validation.flightNumber', { leg: legLabel });
    }
    if (!leg.airport?.trim()) return t('ticketTravel.validation.airport', { leg: legLabel });
  }
  if (leg.transportMode === 'bus' && !leg.busDeparturePlace?.trim()) {
    return t('ticketTravel.validation.busDeparture', { leg: legLabel });
  }

  return null;
}

export function validateTravelFormClient(rows, waiverAccepted, t) {
  if (waiverAccepted !== true) {
    return t('ticketTravel.validation.waiver');
  }

  for (const row of rows) {
    const person = `${row.firstName} ${row.lastName}`.trim();
    const outboundError = validateTravelLegClient(row.outbound, `${person} — ${t('ticketTravel.outboundTitle')}`, t);
    if (outboundError) return outboundError;
    const returnError = validateTravelLegClient(row.returnLeg, `${person} — ${t('ticketTravel.returnTitle')}`, t);
    if (returnError) return returnError;
  }

  return null;
}
